// [Codex 2026-07-09] Voice CAO drawer and control subcomponents.
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  ArrowUp,
  ChevronDown,
  EyeOff,
  FileText,
  MessageSquareText,
  Square,
} from "lucide-react";
import { BrailleSpinner } from "../chat/primitive/BrailleSpinner";
import {
  ConnectorCallList,
  MiniResponse,
} from "../../voice-cao-dock-responses";
import {
  buildTopMeterCells,
  type VoiceDockMeterSpeaker,
} from "../../voice-cao-dock-meter";
import type { ResponseScenario } from "../../voice-cao-dock-data";

interface TopWaveDrawerProps {
  volume: number;
  phase: number;
  speaker: VoiceDockMeterSpeaker;
  isProcessingRequest: boolean;
  isTextReplyMode: boolean;
  isSendEnabled: boolean;
  onToggleTextReplyMode: () => void;
  onStopVoice: () => void;
  onSendVoiceInput: () => void;
}

interface VoiceDockControlsProps {
  isTextReplyMode: boolean;
  onOpenSideChat: () => void;
  onToggleTextReplyMode: () => void;
  onHideWidget: () => void;
}

interface BottomContextDrawerProps {
  response: ResponseScenario;
  isTextReplyMode: boolean;
  isExpanded: boolean;
  isVisible: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  onOpenSideChat: () => void;
}

export function TopWaveDrawer({
  volume,
  phase,
  speaker,
  isProcessingRequest,
  isTextReplyMode,
  isSendEnabled,
  onToggleTextReplyMode,
  onStopVoice,
  onSendVoiceInput,
}: TopWaveDrawerProps) {
  const isLiveUserSpeaking = speaker === "user" && volume > 0.11;
  const [hasRecentUserSpeech, setHasRecentUserSpeech] = useState(false);
  const meterCells = useMemo(
    () => buildTopMeterCells(volume, phase, speaker),
    [phase, speaker, volume],
  );

  useEffect(() => {
    if (speaker !== "user") {
      setHasRecentUserSpeech(false);
      return;
    }
    if (isLiveUserSpeaking) {
      setHasRecentUserSpeech(true);
      return;
    }
    if (!hasRecentUserSpeech) return;
    const timer = window.setTimeout(() => setHasRecentUserSpeech(false), 1400);
    return () => window.clearTimeout(timer);
  }, [hasRecentUserSpeech, isLiveUserSpeaking, speaker]);

  return (
    <div
      className="voice-cao-top-drawer"
      data-open="true"
      data-mode={isTextReplyMode ? "text" : "voice"}
      data-speaker={speaker}
      data-processing={isProcessingRequest ? "true" : "false"}
      data-user-streaming={hasRecentUserSpeech ? "true" : "false"}
      data-agent-volume={volume.toFixed(2)}
      style={{
        animation: "none",
        opacity: 1,
        transform: "translateX(-50%) translateY(0)",
      }}
    >
      <button
        type="button"
        className="voice-cao-meter-toggle"
        aria-pressed={isTextReplyMode}
        aria-label={
          isTextReplyMode
            ? "Switch Voice CAO back to voice reply mode"
            : "Mute Voice CAO and switch to text reply mode"
        }
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onToggleTextReplyMode}
      >
        <div className="voice-cao-pixel-meter" aria-hidden="true">
          {meterCells.map((cell) => (
            <span
              key={cell.key}
              data-lit={cell.lit ? "true" : "false"}
              style={
                {
                  "--cell-index": cell.column,
                  "--cell-row": cell.row,
                  "--cell-intensity": cell.intensity.toFixed(2),
                  "--cell-alpha": (0.2 + cell.intensity * 0.62).toFixed(2),
                } as CSSProperties
              }
            />
          ))}
        </div>
      </button>
      <div
        className="voice-cao-live-controls"
        aria-label="Voice input controls"
      >
        <button
          type="button"
          className="voice-cao-live-control voice-cao-live-control--stop"
          data-voice-control="stop"
          aria-label="Stop voice chat"
          title="Stop"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={onStopVoice}
        >
          <Square
            className="voice-cao-live-control__stop-icon"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          className="voice-cao-live-control voice-cao-live-control--send"
          data-voice-control="send"
          aria-label="Send voice input"
          title="Send"
          disabled={!isSendEnabled}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={onSendVoiceInput}
        >
          <ArrowUp
            className="voice-cao-live-control__send-icon"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
}

export function VoiceDockControls({
  isTextReplyMode,
  onOpenSideChat,
  onToggleTextReplyMode,
  onHideWidget,
}: VoiceDockControlsProps) {
  return (
    <div className="voice-cao-actions">
      <button
        type="button"
        aria-label="Continue in Side Chat"
        title="Continue in Side Chat"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onOpenSideChat}
      >
        <MessageSquareText />
      </button>
      <button
        type="button"
        aria-label="Switch to Text Replies"
        title="Switch to Text Replies"
        data-active={isTextReplyMode ? "true" : "false"}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onToggleTextReplyMode}
      >
        <FileText />
      </button>
      <button
        type="button"
        aria-label="Hide widget"
        title="Hide widget"
        onPointerDown={(event) => {
          event.stopPropagation();
          onHideWidget();
        }}
        onClick={onHideWidget}
      >
        <EyeOff />
      </button>
    </div>
  );
}

export function BottomContextDrawer({
  response,
  isTextReplyMode,
  isExpanded,
  isVisible,
  onExpandedChange,
  onOpenSideChat,
}: BottomContextDrawerProps) {
  return (
    <div
      className="voice-cao-bottom-drawer"
      data-kind={response.kind}
      data-open={isExpanded ? "true" : "false"}
      data-visible={isVisible ? "true" : "false"}
    >
      <button
        type="button"
        className="voice-cao-thinking-trigger"
        aria-expanded={isExpanded}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => onExpandedChange(!isExpanded)}
      >
        <BrailleSpinner
          className="voice-cao-spinner"
          size={11.2}
          label={`${response.phrase} in progress`}
        />
        <span className="voice-cao-thinking-label">{response.phrase}</span>
        <ChevronDown className="voice-cao-chevron" />
      </button>

      <div
        className="voice-cao-thinking-stream"
        data-open={isExpanded ? "true" : "false"}
        aria-hidden={isExpanded ? "false" : "true"}
      >
        <div className="voice-cao-thinking-stream-inner">
          <p>{response.stream}</p>
          <ConnectorCallList response={response} />
          <MiniResponse
            response={response}
            isTextReplyMode={isTextReplyMode}
            onOpenSideChat={onOpenSideChat}
          />
        </div>
      </div>
    </div>
  );
}
